import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Logo from "../components/Logo";
import LanguageSwitcher from "../components/LanguageSwitcher";
import Footer from "../components/Footer";
import { useLang } from "../context/LangContext";

const STRINGS = {
  ca: {
    kicker: "Navegació",
    title: "Mapa del lloc",
    app: "L'aplicació",
    legal: "Legal",
    account: "El meu compte",
  },
  es: {
    kicker: "Navegación",
    title: "Mapa del sitio",
    app: "La aplicación",
    legal: "Legal",
    account: "Mi cuenta",
  },
  en: {
    kicker: "Navigation",
    title: "Sitemap",
    app: "The app",
    legal: "Legal",
    account: "My account",
  },
};

export default function SitemapPage() {
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const s = STRINGS[lang] || STRINGS.es;

  const sections = [
    {
      title: s.app,
      links: [
        { to: "/", label: { ca: "Benvinguda", es: "Bienvenida", en: "Welcome" } },
        { to: "/home", label: { ca: "Inici", es: "Inicio", en: "Home" } },
        { to: "/calendar", label: { ca: "Calendari", es: "Calendario", en: "Calendar" } },
        { to: "/experiences/empuries", label: { ca: "Ruïnes d'Empúries", es: "Ruinas de Empúries", en: "Empúries" } },
        { to: "/experiences/marimurtra", label: { ca: "Convent + Marimurtra", es: "Convento + Marimurtra", en: "Sant Francesc + Marimurtra" } },
        { to: "/experiences/rusinol", label: { ca: "Colònia Rusiñol", es: "Colonia Rusiñol", en: "Rusiñol Colony" } },
        { to: "/experiences/escornalbou", label: { ca: "Castell d'Escornalbou", es: "Castillo de Escornalbou", en: "Escornalbou Castle" } },
        { to: "/experiences/artigas", label: { ca: "Asland + Jardins Artigas", es: "Asland + Jardines Artigas", en: "Asland + Artigas Gardens" } },
      ],
    },
    {
      title: s.account,
      links: [
        { to: "/auth?mode=login", label: { ca: "Iniciar sessió", es: "Iniciar sesión", en: "Sign in" } },
        { to: "/auth?mode=register", label: { ca: "Registrar-se", es: "Registrarse", en: "Register" } },
        { to: "/trips", label: { ca: "Els meus viatges", es: "Mis viajes", en: "My trips" } },
        { to: "/profile", label: { ca: "Perfil", es: "Perfil", en: "Profile" } },
        { to: "/profile/notifications", label: { ca: "Notificacions", es: "Notificaciones", en: "Notifications" } },
      ],
    },
    {
      title: s.legal,
      links: [
        { to: "/legal/privacy", label: { ca: "Política de privacitat", es: "Política de privacidad", en: "Privacy policy" } },
        { to: "/legal/cookies", label: { ca: "Política de galetes", es: "Política de cookies", en: "Cookie policy" } },
        { to: "/legal/legal-notice", label: { ca: "Avís legal", es: "Aviso legal", en: "Legal notice" } },
        { to: "/legal/terms", label: { ca: "Termes i condicions", es: "Términos y condiciones", en: "Terms & conditions" } },
      ],
    },
  ];

  return (
    <div className="bg-brand-cream min-h-[100dvh]">
      <div className="border-b border-brand-tan/25 bg-brand-cream/85 backdrop-blur-xl sticky top-0 z-30">
        <div className="mx-auto max-w-5xl px-6 md:px-12 py-5 flex items-center justify-between gap-4">
          <button
            onClick={() => navigate(-1)}
            data-testid="sitemap-back"
            className="inline-flex items-center gap-2 text-brand-brown/70 text-xs tracking-[0.25em] uppercase font-bold hover:text-brand-brown"
          >
            <ArrowLeft size={14} strokeWidth={1.75} />
            ARISTEIA
          </button>
          <Logo size="sm" />
          <LanguageSwitcher />
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-6 md:px-12 py-12 md:py-20">
        <p className="text-[10px] tracking-[0.45em] uppercase font-bold text-brand-tan">
          {s.kicker}
        </p>
        <h1
          className="font-serif text-4xl md:text-6xl leading-[1.04] text-brand-brown mt-3"
          data-testid="sitemap-title"
        >
          {s.title}
        </h1>

        <div className="mt-14 grid gap-12 md:grid-cols-3">
          {sections.map((sec) => (
            <section key={sec.title}>
              <p className="text-[10px] tracking-[0.4em] uppercase font-bold text-brand-tan border-b border-brand-tan/30 pb-3">
                {sec.title}
              </p>
              <ul className="mt-5 space-y-3">
                {sec.links.map((l) => (
                  <li key={l.to}>
                    <Link
                      to={l.to}
                      data-testid={`sitemap-link-${l.to.replace(/[^a-z0-9]/gi, "-")}`}
                      className="text-brand-brown hover:text-brand-deep-brown font-medium underline-offset-4 hover:underline decoration-brand-tan"
                    >
                      {l.label[lang] || l.label.es}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
}
